import React, {
  useState,
  useEffect,
  useMemo,
  forwardRef,
  useImperativeHandle
} from 'react'
import {
  Input,
  Icon,
  Tooltip,
  message,
  Spin,
  Select,
  Button,
  Popover,
  Alert,
  Card,
  Collapse,
  Table,
  Avatar
} from 'antd'
import classNames from 'classnames'
import {
  SortableContainer,
  SortableElement,
  sortableHandle,
  arrayMove as arrayMoveImmutable
} from 'react-sortable-hoc'

import StepTitle from '../StepTitle'

import './ShotList.scss'
import {
  generateShots,
  mapCharacterToShots,
  getAvailableCharacters,
  groupShotsToScenes,
  getTaskStatus
} from '../../../../services/api/aiVideoGeneration'
import { set } from 'lodash'
import AddCharacterModal from './components/AddCharacterModal'
import CharacterMappingTable from './components/CharacterMappingTable'

const { Option } = Select
const { Panel } = Collapse


// Drag Handle Component
const DragHandle = sortableHandle(() => (
  <Tooltip title="Drag to reorder">
    <Icon
      type="drag"
      className="drag-handle"
      style={{ cursor: 'grab', marginRight: 10, color: '#999' }}
    />
  </Tooltip>
))

// Character Highlighter Component (read-only, just highlights)
const CharacterHighlighter = ({ text, selectedCharacters = [] }) => {
  // Create a mapping of current character names to their original placeholders
  const characterMapping = useMemo(() => {
    const mapping = {}
    selectedCharacters.forEach(char => {
      const originalKey = char.original && char.original.replace(/^__|__$/g, '') || ''
      const currentKey = char.current && char.current.replace(/^__|__$/g, '') || ''
      if (originalKey && currentKey) {
        mapping[currentKey] = char.original
        // Also map the original key to itself for backward compatibility
        mapping[originalKey] = char.original
      }
    })
    return mapping
  }, [selectedCharacters])

  // Find character names in text (both original placeholders and current mapped names)
  const highlightCharacters = (text) => {
    if (!text || typeof text !== 'string') return text

    let processedText = text
    const highlights = []

    // First, find original placeholder patterns
    const placeholderRegex = /(_{2}|\*{2})([^_*]+)(_{2}|\*{2})/g
    let match
    while ((match = placeholderRegex.exec(text)) !== null) {
      highlights.push({
        start: match.index,
        end: match.index + match[0].length,
        text: match[0],
        isPlaceholder: true
      })
    }

    // Then find mapped character names (current names from selectedCharacters)
    selectedCharacters.forEach(char => {
      if (char.current) {
        const currentName = char.current.replace(/^__|__$/g, '')
        const regex = new RegExp(`\\b${currentName}\\b`, 'g')
        let nameMatch
        while ((nameMatch = regex.exec(text)) !== null) {
          // Check if this position is already covered by a placeholder
          const isOverlapping = highlights.some(h =>
            nameMatch.index >= h.start && nameMatch.index < h.end
          )
          if (!isOverlapping) {
            highlights.push({
              start: nameMatch.index,
              end: nameMatch.index + currentName.length,
              text: currentName,
              isPlaceholder: false
            })
          }
        }
      }
    })

    // Sort highlights by position
    highlights.sort((a, b) => a.start - b.start)

    if (highlights.length === 0) return text

    // Build the highlighted text
    const parts = []
    let lastIndex = 0

    highlights.forEach((highlight, idx) => {
      // Add text before highlight
      if (highlight.start > lastIndex) {
        parts.push(text.substring(lastIndex, highlight.start))
      }

      // Add highlighted text
      parts.push(
        <span
          key={`highlight-${idx}`}
          className="character-placeholder"
          style={{
            backgroundColor: '#e6f7ff',
            padding: '2px 4px',
            borderRadius: '3px',
            border: '1px solid #91d5ff'
          }}
        >
          {highlight.text}
        </span>
      )

      lastIndex = highlight.end
    })

    // Add remaining text
    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex))
    }

    return parts
  }

  return (
    <div className="character-highlighter">
      <div className="shot-direction-text">
        {highlightCharacters(text)}
      </div>
    </div>
  )
}

// Read-only display for other fields
const ReadOnlyField = ({ text }) => {
  return <div className="readonly-field">{text}</div>
}

// Shot Component (not sortable)
const Shot = ({
  shot,
  shotIndex,
  expandedPanelId,
  setExpandedPanelId,
  selectedCharacters
}) => {
  const { id, title, location, scenery, shot_direction, dialogue } = shot

  return (
    <div className="shot-list-shot">
      <div className="shot-header">
        <div
          className="shot-title-container"
          onClick={() => setExpandedPanelId(expandedPanelId === id ? null : id)}
        >
          <span>Shot {shotIndex + 1}</span>
          <Icon
            type="right"
            rotate={expandedPanelId === id ? 90 : 0}
            className="expand-icon"
          />
        </div>

        <ReadOnlyField text={location} />
      </div>

      <div
        className={classNames('shot-description-container', {
          expanded: expandedPanelId === id
        })}
      >
        <div className="shot-detail-field">
          <label>Title:</label>
          <ReadOnlyField text={title} />
        </div>

        <div className="shot-detail-field">
          <label>Scenery:</label>
          <div className="readonly-field multiline">{scenery}</div>
        </div>

        <div className="shot-detail-field">
          <label>Shot Direction:</label>
          <CharacterHighlighter
            text={shot_direction}
            selectedCharacters={selectedCharacters}
          />
        </div>

        {Object.keys(dialogue || {}).length > 0 && (
          <div className="shot-detail-field">
            <label>Dialogue:</label>
            {Object.entries(dialogue || {}).map(([character, line], idx) => (
              <div key={idx} className="dialogue-entry readonly">
                <div className="dialogue-character">
                  <CharacterHighlighter
                    text={character}
                    selectedCharacters={selectedCharacters}
                  />:
                </div>
                <div className="dialogue-line">
                  <CharacterHighlighter
                    text={typeof line === 'object' ? line.text : line}
                    selectedCharacters={selectedCharacters}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// Sortable Scene Component
const SortableScene = SortableElement(
  ({
    scene,
    sceneIndex,
    expandedPanelId,
    setExpandedPanelId,
    selectedCharacters
  }) => {
    const [isExpanded, setIsExpanded] = useState(false)

    return (
      <Card
        className="scene-card"
        title={
          <div className="scene-header">
            <DragHandle />
            <span>{scene.title.toUpperCase()}</span>
          </div>
        }
        extra={
          <Button
            type="link"
            onClick={() => setIsExpanded(!isExpanded)}
            icon={isExpanded ? 'up' : 'down'}
          >
            {isExpanded ? 'Collapse' : 'Expand'}
          </Button>
        }
      >
        <div className="scene-info">
          <div className="scene-detail-field">
            <label>Location:</label>
            <ReadOnlyField text={scene.description} />
          </div>

          {isExpanded && (
            <div className="scene-shots">
              <h4>Shots ({scene.shots.length})</h4>
              {scene.shots.map((shot, index) => (
                <Shot
                  key={`${scene.id}-shot-${index}`}
                  shot={shot}
                  shotIndex={index}
                  expandedPanelId={expandedPanelId}
                  setExpandedPanelId={setExpandedPanelId}
                  selectedCharacters={selectedCharacters}
                />
              ))}
            </div>
          )}
        </div>
      </Card>
    )
  }
)

// Sortable Scene List Component
const SortableSceneList = SortableContainer(
  ({
    scenes,
    expandedPanelId,
    setExpandedPanelId,
    selectedCharacters
  }) => {
    return (
      <div className="scene-list">
        {scenes.map((scene, index) => (
          <SortableScene
            key={`scene-${index}`}
            index={index}
            scene={scene}
            sceneIndex={index}
            expandedPanelId={expandedPanelId}
            setExpandedPanelId={setExpandedPanelId}
            selectedCharacters={selectedCharacters}
          />
        ))}
      </div>
    )
  }
)

// Custom function to render the dragged element
const dragHandleHelper = (node, parent) => {
  const clone = node.cloneNode(true)

  // Find and remove expansion details during drag
  const shotsContainer = clone.querySelector('.scene-shots')
  if (shotsContainer) {
    shotsContainer.remove()
  }

  clone.className += ' dragging-scene'
  parent.appendChild(clone)

  return clone
}

const MAX_RETRIES = 10

const ShotList = forwardRef((props, ref) => {
  const {
    formData,
    setFormData,
    onValidationChange,
    onProceed,
    onEnableProcessShots,
    onDisableProcessShots,
    onFineTunedLoading
  } = props

  const [scenes, setScenes] = useState([])
  const [expandedPanelId, setExpandedPanelId] = useState(null)
  const [isShotLoading, setIsShotLoading] = useState(true)
  const [isCharacterMapLoading, setIsCharacterMapLoading] = useState(false)
  const [characters, setCharacters] = useState([])
  const [changedCharacters, setChangedCharacters] = useState([])
  const [initialCharacters, setInitialCharacters] = useState([])
  const [apiError, setApiError] = useState(false)
  const [retryCount, setRetryCount] = useState(0)
  const [errorLog, setErrorLog] = useState(null)
  const [isFetchingCharacters, setIsFetchingCharacters] = useState(false)
  const [showAddCharacterModal, setShowAddCharacterModal] = useState(false)
  const [clustering, setIsClustering] = useState(false)

  const {
    characterList = [],
    shotList = [],
    shotLoading = false,
    shotsApiRes = {},
    selectedCharacter = [] // This will persist character mappings
  } = formData

  useEffect(() => {
    // Extract characters from formData
    if (characterList && characterList.length > 0) {
      setCharacters(formData.characterList)
    } else {
      fetchExistingCharacters()
    }

    // Restore character mappings from formData
    if (selectedCharacter && selectedCharacter.length > 0) {
      setChangedCharacters(selectedCharacter)
      setInitialCharacters(selectedCharacter)
    }

    if (shotList.length > 0) {
      setScenes(shotList)
      setIsShotLoading(false)
    } else if (shotLoading) {
      return
    } else {
      fetchShots()
    }
  }, [])

  useEffect(() => {
    if (shotList.length > 0) {
      setScenes(shotList)
      setIsShotLoading(false)
    }
  }, [shotList])

  useEffect(() => {
    if (characters.length > 0) {
      setFormData({ ...formData, characterList: characters })
    }
  }, [characters])

  useEffect(() => {
    if (changedCharacters.length > 0) {
      setFormData({ ...formData, selectedCharacter: changedCharacters })
    }
  }, [changedCharacters])

  useImperativeHandle(ref, () => ({
    processShots: () => {
      generateFineTunedShots()
    }
  }))

  async function fetchExistingCharacters() {
    try {
      setIsFetchingCharacters(true)
      const response = await getAvailableCharacters()

      const taskId = response && response.data && (response.data['task-id'] || response.data.task_id || response.data.taskId)
      if (!taskId) {
        throw new Error('Missing task-id in getAvailableCharacters response')
      }

      const charArray = await waitForTaskOutput(taskId)
      if (!Array.isArray(charArray)) {
        throw new Error('Invalid response format: output is not an array')
      }

      setCharacters(
        charArray.filter(Boolean).map((item, index) => ({
          ...item,
          id: index + 1
        }))
      )
    } catch (error) {
      console.error('Error fetching existing characters:', error)
    } finally {
      setIsFetchingCharacters(false)
    }
  }

  async function waitForTaskOutput(taskId, attempt = 0) {
    if (attempt >= MAX_RETRIES) {
      throw new Error('Max retries reached while waiting for task output')
    }

    try {
      const statusRes = await getTaskStatus(taskId)
      const output = statusRes && statusRes.data && statusRes.data.output
      if (Array.isArray(output) && output.length > 0) {
        return output
      }
    } catch (err) {
      console.error('Error checking task status:', err)
    }

    await new Promise(function(resolve) { setTimeout(resolve, 1000) })
    return waitForTaskOutput(taskId, attempt + 1)
  }

  const fetchShots = async (retryCount = 0, errorLog = []) => {
    setIsShotLoading(true)
    const storyText = formData && formData.voiceoverText

    try {
      setRetryCount(retryCount)
      const res = await generateShots({ story: storyText })
      const shotsData = res.data
      handleScenesMapping(shotsData)
      setApiError(false)
      setErrorLog(null)
    } catch (err) {
      const errorMsg = err.message || String(err)
      setApiError(true)
      message.error('Failed to generate shots: ' + errorMsg)

      setErrorLog(errorMsg)

      if (retryCount < MAX_RETRIES) {
        setTimeout(() => fetchShots(retryCount + 1, errorMsg), 1000)
      } else {
        setIsShotLoading(false)
        message.error('Max retries reached. Please try again later.')
      }
    }
  }

  // Enhanced function to extract characters from both original and current text
  function extractCharacters(shots, selectedCharacters = []) {
    const characterSet = new Set()
    const regex = /(_{2}|\*{2})([^_*]+)(_{2}|\*{2})/g

    const processText = text => {
      if (typeof text !== 'string') return

      let match
      const regexCopy = new RegExp(regex)

      while ((match = regexCopy.exec(text)) !== null) {
        characterSet.add(`${match[1]}${match[2]}${match[3]}`)
      }
    }

    // Process each shot
    for (const shotKey in shots) {
      const shot = shots[shotKey]

      // Check shot direction
      if (shot['shot direction'] || shot['shot_direction']) {
        processText(shot['shot direction'] || shot['shot_direction'])
      }

      // Check dialogue
      if (shot.dialogue) {
        Object.keys(shot.dialogue).forEach(character => {
          processText(character)
        })

        Object.values(shot.dialogue).forEach(line => {
          const text = typeof line === 'object' ? line.text : line
          processText(text)
        })
      }
    }

    // Add characters from selectedCharacters if they exist (for when coming back from next step)
    selectedCharacters && selectedCharacters.length > 0 && selectedCharacters.forEach(char => {
      if (char.original) {
        characterSet.add(char.original)
      }
      // Also check for current character names in the text
      if (char.current) {
        const currentName = char.current.replace(/^__|__$/g, '')
        // Search for current character names in the text and add them as original placeholders
        for (const shotKey in shots) {
          const shot = shots[shotKey]

          const shotDirection = shot['shot direction'] || shot['shot_direction']
          // Check shot direction for current character names
          if (shotDirection && shotDirection.includes(currentName)) {
            characterSet.add(char.original)
          }

          // Check dialogue for current character names
          if (shot.dialogue) {
            Object.keys(shot.dialogue).forEach(character => {
              if (character.includes(currentName)) {
                characterSet.add(char.original)
              }
            })

            Object.values(shot.dialogue).forEach(line => {
              const text = typeof line === 'object' ? line.text : line
              if (text && text.includes(currentName)) {
                characterSet.add(char.original)
              }
            })
          }
        }
      }
    })

    return Array.from(characterSet)
  }

  // Enhanced character replacement that works with both original and mapped names
  const replaceCharacterInText = (text, oldChar, newChar, selectedCharacters) => {
    if (!text) return text

    let newText = text

    // Find the mapping for this character
    const mapping = selectedCharacters.find(char =>
      char.original && char.original.replace(/^__|__$/g, '') === oldChar.replace(/^__|__$/g, '') ||
      char.current && char.current.replace(/^__|__$/g, '') === oldChar.replace(/^__|__$/g, '')
    )

    if (mapping) {
      // Replace both original and current forms
      const originalName = mapping.original && mapping.original.replace(/^__|__$/g, '') || ''
      const currentName = mapping.current && mapping.current.replace(/^__|__$/g, '') || ''

      // Replace placeholder patterns
      const placeholderRegex1 = new RegExp(`__${originalName}__`, 'g')
      const placeholderRegex2 = new RegExp(`\\*\\*${originalName}\\*\\*`, 'g')
      const wordRegex = new RegExp(`\\b${currentName}\\b`, 'g')

      newText = newText.replace(placeholderRegex1, newChar)
      newText = newText.replace(placeholderRegex2, newChar)
      newText = newText.replace(wordRegex, newChar)
    }

    // Also handle direct character name replacements
    const charRegex = new RegExp(`\\b${oldChar}\\b`, 'g')
    newText = newText.replace(charRegex, newChar)

    return newText
  }

  // Enhanced character change handler that works from CharacterMappingTable
  const handleCharacterChange = (oldCharacter, newCharacter, firstCharacter) => {
    console.log(`Replacing "${oldCharacter}" with "${newCharacter}"`)

    const oldCharWithUnderscores = `__${oldCharacter.replace(/^__|__$/g, '')}__`
    const newCharWithUnderscores = `__${newCharacter}__`

    // Create a deep copy of scenes and update all text
    const updatedScenes = scenes.map(scene => {
      const updatedShots = scene.shots.map(shot => {
        const updatedShot = {
          ...shot,
          title: replaceCharacterInText(shot.title, oldCharacter, newCharacter, changedCharacters),
          location: replaceCharacterInText(shot.location, oldCharacter, newCharacter, changedCharacters),
          scenery: replaceCharacterInText(shot.scenery, oldCharacter, newCharacter, changedCharacters),
          shot_direction: replaceCharacterInText(shot.shot_direction, oldCharacter, newCharacter, changedCharacters),
          characters: Array.from(new Set([...(shot.characters || []), newCharacter]))
        }

        // Update dialogue
        if (shot.dialogue) {
          const updatedDialogue = {}

          Object.entries(shot.dialogue).forEach(([character, line]) => {
            let newKey = character

            // Handle character key replacement
            if (character.includes(oldCharacter) || character === oldCharWithUnderscores) {
              newKey = character.includes('__') ? newCharWithUnderscores : newCharacter
            }

            // Update dialogue content
            const dialogueContent = typeof line === 'object' ? line.text : line
            const updatedContent = replaceCharacterInText(dialogueContent, oldCharacter, newCharacter, changedCharacters)

            updatedDialogue[newKey] = typeof line === 'object'
              ? { ...line, text: updatedContent }
              : updatedContent
          })

          updatedShot.dialogue = updatedDialogue
        }

        return updatedShot
      })

      return {
        ...scene,
        title: replaceCharacterInText(scene.title, oldCharacter, newCharacter, changedCharacters),
        description: replaceCharacterInText(scene.description, oldCharacter, newCharacter, changedCharacters),
        shots: updatedShots
      }
    })

    setScenes(updatedScenes)
    setFormData(prev => ({ ...prev, shotList: updatedScenes }))

    // Update character mappings
    let updatedChars = [...changedCharacters]
    const existingCharIndex = updatedChars.findIndex(
      char => char.original === oldCharWithUnderscores ||
        char.original && char.original.replace(/^__|__$/g, '') === oldCharacter.replace(/^__|__$/g, '')
    )

    if (existingCharIndex !== -1) {
      updatedChars[existingCharIndex] = {
        ...updatedChars[existingCharIndex],
        last: updatedChars[existingCharIndex].current,
        current: newCharWithUnderscores
      }
    } else {
      updatedChars.push({
        original: oldCharWithUnderscores,
        last: oldCharWithUnderscores,
        current: newCharWithUnderscores
      })
    }

    setChangedCharacters(updatedChars)
    message.success(`Changed "${oldCharacter}" to "${newCharacter}" throughout all scenes and shots`)
  }

  const onSortEnd = ({ oldIndex, newIndex }) => {
    const updatedScenes = arrayMoveImmutable(scenes, oldIndex, newIndex)
    setScenes(updatedScenes)
    setFormData(prev => ({ ...prev, shotList: updatedScenes }))
  }

  // Get total characters including those from original shots and selectedCharacter
  const totalCharacters = useMemo(() => {
    const originalChars = extractCharacters(shotsApiRes || {}, selectedCharacter)
    return Array.from(new Set(originalChars))
  }, [shotsApiRes, selectedCharacter])

  const handleFineTunedShotsResponse = shotsData => {
    setIsShotLoading(true)

    const processedShotsData = { ...shotsData }

    Object.keys(processedShotsData).forEach(shotKey => {
      const shot = processedShotsData[shotKey]
      const charactersInShot = new Set()

      if (shot['shot_direction']) {
        const shotDirectionCharacters = extractCharactersFromText(shot['shot_direction'])
        shotDirectionCharacters.forEach(char => charactersInShot.add(char))
      }

      if (shot.dialogue) {
        Object.keys(shot.dialogue).forEach(character => {
          const cleanCharacter = character.replace(/(_{2}|\*{2})([^_*]+)(_{2}|\*{2})/, '$2')
          charactersInShot.add(cleanCharacter)
        })

        Object.values(shot.dialogue).forEach(line => {
          const text = typeof line === 'object' ? line.text : line
          if (text) {
            const dialogueCharacters = extractCharactersFromText(text)
            dialogueCharacters.forEach(char => charactersInShot.add(char))
          }
        })
      }

      processedShotsData[shotKey].characters = Array.from(charactersInShot)
    })

    handleScenesMapping(processedShotsData, true)
  }

  const extractCharactersFromText = text => {
    if (!text || typeof text !== 'string') return []

    const characters = new Set()
    const regex = /(_{2}|\*{2})([^_*]+)(_{2}|\*{2})/g
    let match

    while ((match = regex.exec(text)) !== null) {
      characters.add(match[2])
    }

    return Array.from(characters)
  }

  const formatSceneObj = sceneObj => {
    return Object.entries(sceneObj).map(([sceneKey, sceneValue]) => {
      const { scene_location = '', shots = [] } = sceneValue

      const formattedShots = shots.map((shotObj, index) => {
        const shotKey = Object.keys(shotObj)[0]
        const shotValue = shotObj[shotKey]

        return {
          id: `${sceneKey}-shot-${index + 1}`,
          key: shotKey,
          title: shotValue.title || '',
          location: shotValue.location || '',
          scenery: shotValue.scenery || '',
          shot_direction: shotValue.shot_direction || shotValue['shot direction'] || '',
          dialogue: shotValue.dialogue || {},
          characters: shotValue.characters || []
        }
      })

      return {
        id: sceneKey,
        title: sceneKey,
        description: scene_location,
        shots: formattedShots
      }
    })
  }

  const generateFineTunedShots = () => {
    onFineTunedLoading && onFineTunedLoading()

    const characterDict = changedCharacters.reduce((acc, char) => {
      const cleanedName = char.current.replace(/^__|__$/g, '')
      const characterData = characters.find(
        c => c.name.toLowerCase() === cleanedName.toLowerCase()
      )

      acc[char.original] = {
        name: cleanedName,
        gender: characterData ? characterData.gender : 'unknown'
      }

      return acc
    }, {})

    const payload = {
      script: shotsApiRes || formData.shotsApiRes,
      character_dict: characterDict
    }

    handleCharacterMapping(payload)
  }

  const handleCharacterMapping = async payload => {
    try {
      setIsCharacterMapLoading(true)
      const res = await mapCharacterToShots(payload)
      const newShots = res.data && res.data.modified_script

      handleFineTunedShotsResponse(newShots)
    } catch (error) {
      onFineTunedLoading && onFineTunedLoading()
      message.error('Character mapping failed. Please Try Again !')
      console.error('Character mapping failed:', error)
    } finally {
      setIsCharacterMapLoading(false)
      setChangedCharacters([])
      onValidationChange(true)
    }
  }

  const handleScenesMapping = async (data, isFineTuned) => {
    try {
      setIsClustering(true)
      const payload = {
        input_json_data: data
      }

      const response = await groupShotsToScenes(payload)

      // Always expect a task id and poll until completion
      const taskId = response && response.data && (response.data.task_id || response.data['task-id'] || response.data.taskId)
      if (!taskId) {
        throw new Error('Missing task_id in scene grouping response')
      }

      await pollSceneTaskStatus(taskId, data, !!isFineTuned)
    } catch (error) {
      message.error('Scene mapping failed. Please Try Again !')
      console.error('Scene mapping failed:', error)
      setIsShotLoading(false)
      setIsClustering(false)
    }
  }

  const pollSceneTaskStatus = async (taskId, originalData, isFineTuned, retryCount = 0) => {
    try {
      const response = await getTaskStatus(taskId)

      if (response && response.data && response.data.status === 'success' && response.data.output) {
        const sceneData = response.data.output
        const formattedSceneData = formatSceneObj(sceneData)

        setScenes(formattedSceneData)
        setFormData(prev => ({
          ...prev,
          shotList: formattedSceneData,
          shotsApiRes: originalData
        }))

        setIsShotLoading(false)
        setIsClustering(false)
        if (isFineTuned) {
          onProceed()
        }
      } else if (response && response.data && response.data.status === 'failed') {
        console.error('Task failed:', response.data)
        message.error('Scene mapping failed. Please Try Again !')
        setIsShotLoading(false)
        setIsClustering(false)
      } else if (retryCount < MAX_RETRIES) {
        setTimeout(function() { pollSceneTaskStatus(taskId, originalData, isFineTuned, retryCount + 1) }, 2000)
      } else {
        console.error('Max retries exceeded for task:', taskId)
        message.error('Scene mapping timeout. Please Try Again !')
        setIsShotLoading(false)
        setIsClustering(false)
      }
    } catch (error) {
      if (retryCount < MAX_RETRIES) {
        setTimeout(function() { pollSceneTaskStatus(taskId, originalData, isFineTuned, retryCount + 1) }, 2000)
      } else {
        console.error('Error polling task status:', error)
        message.error('Error while polling scene mapping. Please Try Again !')
        setIsShotLoading(false)
        setIsClustering(false)
      }
    }
  }

  const isAllCharactersChanged = totalCharacters.length && changedCharacters.length === totalCharacters.length

  const hasChanges = useMemo(() => {
    if (changedCharacters.length !== initialCharacters.length) return true;

    const normalize = (arr) =>
      arr
        .slice()
        .sort((a, b) => a.original.localeCompare(b.original))
        .map(({ original, current }) => ({ original, current }));

    const a = normalize(changedCharacters);
    const b = normalize(initialCharacters);

    return JSON.stringify(a) !== JSON.stringify(b);
  }, [changedCharacters, initialCharacters]);

  useEffect(() => {
    if (changedCharacters.length && isAllCharactersChanged && hasChanges && onEnableProcessShots) {
      onEnableProcessShots()
    } else {
      onDisableProcessShots()
    }
  }, [isAllCharactersChanged, hasChanges])

  const handleAddCharacter = () => {
    setShowAddCharacterModal(true)
  }

  const handleCharacterAddition = character => {
    fetchExistingCharacters()
  }

  if (isShotLoading || isFetchingCharacters || isCharacterMapLoading) {
    return (
      <div className="shot-list-shots shot-list-loading">
        <Spin size="large" spinning />
        <p className="shot-list-loading-text">
          {clustering
            ? 'Processing your scenes...'
            : isFetchingCharacters
              ? 'Fetching characters...'
              : isCharacterMapLoading
                ? 'Mapping characters to shots...'
                : `Generating your shots${retryCount ? ` (Retry ${retryCount})` : ''}...`}
        </p>
        <p className="shot-list-loading-subtext">
          Please wait! This may take a few minutes.
        </p>
        {retryCount > 0 && (
          <blockquote style={{ marginTop: '1rem', color: 'gray', fontSize: '0.8rem' }}>
            Last Error: {errorLog}
          </blockquote>
        )}
      </div>
    )
  }

  if (apiError) {
    return (
      <div className="shot-list-shots shot-list-loading" style={{ flexDirection: 'column', gap: '1rem' }}>
        <Alert message="Failed to generate shots. Try again !" type="error" />
        <Button type="primary" onClick={() => fetchShots()}>
          Generate Shots
        </Button>
      </div>
    )
  }

  console.log('hasChanges', hasChanges)

  return (
    <div className="shot-list-shots">
      <StepTitle
        iconType="ordered-list"
        title="Shot List"
        description="Map characters first, then drag to reorder scenes."
      />

      {/* New Character Mapping UI */}
      {totalCharacters.length > 0 && (
        <CharacterMappingTable
          placeholderCharacters={totalCharacters}
          characters={characters}
          onCharacterChange={handleCharacterChange}
          changedCharacters={changedCharacters}
          onAddCharacter={handleAddCharacter}
          isFetchingCharacters={isFetchingCharacters}
        />
      )}

      <Alert
        style={{ marginBottom: '1rem' }}
        message={`${changedCharacters.length} out of ${totalCharacters.length} characters mapped. ${hasChanges
          ? 'Changes detected - process shots to apply changes'
          : 'No changes made to character mappings'
          }`}
        type={hasChanges ? "warning" : "info"}
      />

      <SortableSceneList
        scenes={scenes}
        expandedPanelId={expandedPanelId}
        setExpandedPanelId={setExpandedPanelId}
        characters={characters}
        onCharacterChange={handleCharacterChange}
        onSortEnd={onSortEnd}
        useDragHandle
        axis="y"
        helperClass="dragging-scene"
        helperContainer={document.body}
        helper={dragHandleHelper}
      />

      {showAddCharacterModal && (
        <AddCharacterModal
          onClose={() => setShowAddCharacterModal(false)}
          onCreate={handleCharacterAddition}
        />
      )}
    </div>
  )
})

export default ShotList
